import { useState } from 'react';
import { useI18n } from '@/i18n';
import { Card, Icon, Skeleton, Button } from '@/components/ui';
import {
  useDocumentTree,
  type Document,
  type FolderNode,
} from '@/hooks/use-knowledge';

// =============================================================================
// Helpers
// =============================================================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// =============================================================================
// Breadcrumb
// =============================================================================

function FolderBreadcrumb({
  path,
  onNavigate,
}: {
  path: string;
  onNavigate: (folder: string) => void;
}) {
  const segments = path ? path.split('/') : [];

  return (
    <nav className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 mb-3">
      <button
        onClick={() => onNavigate('')}
        className="hover:text-gray-900 dark:hover:text-gray-100 font-medium"
      >
        <Icon name="home" size={14} />
      </button>
      {segments.map((seg, i) => {
        const segPath = segments.slice(0, i + 1).join('/');
        const isLast = i === segments.length - 1;
        return (
          <span key={segPath} className="flex items-center gap-1">
            <Icon name="chevron-right" size={12} />
            {isLast ? (
              <span className="text-gray-900 dark:text-gray-100 font-medium">{seg}</span>
            ) : (
              <button
                onClick={() => onNavigate(segPath)}
                className="hover:text-gray-900 dark:hover:text-gray-100"
              >
                {seg}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
}

// =============================================================================
// Folder Row
// =============================================================================

function FolderRow({
  folder,
  onOpen,
}: {
  folder: FolderNode;
  onOpen: (path: string) => void;
}) {
  return (
    <Card
      className="p-3 hover:shadow-sm transition-shadow cursor-pointer"
      onClick={() => onOpen(folder.path)}
    >
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30">
          <Icon name="folder-open" size={20} className="text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {folder.name}
          </h4>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {folder.document_count} {folder.document_count === 1 ? 'document' : 'documents'}
            {' · '}
            {formatFileSize(folder.total_size_bytes)}
          </span>
        </div>
        <Icon name="chevron-right" size={16} className="text-gray-400" />
      </div>
    </Card>
  );
}

// =============================================================================
// Loading skeleton
// =============================================================================

function TreeSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i} className="p-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

interface CollectionTreeViewProps {
  collectionId: string;
  searchTerm?: string;
  onDocumentClick?: (doc: Document) => void;
  renderDocumentRow: (doc: Document) => React.ReactNode;
}

export function CollectionTreeView({
  collectionId,
  searchTerm = '',
  onDocumentClick: _onDocumentClick,
  renderDocumentRow,
}: CollectionTreeViewProps) {
  const { t } = useI18n();
  const [currentFolder, setCurrentFolder] = useState('');
  const { data: tree, isLoading, isError, refetch } = useDocumentTree(collectionId, currentFolder);

  if (isLoading) {
    return <TreeSkeleton />;
  }

  if (isError) {
    return (
      <Card className="p-8 text-center">
        <Icon name="alert-circle" size={48} className="mx-auto text-red-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          {t('admin.knowledge.documents.loadFailed')}
        </h3>
        <Button variant="outline" onClick={() => refetch()}>
          {t('common.retry')}
        </Button>
      </Card>
    );
  }

  if (!tree) return null;

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const visibleDocuments = normalizedSearch
    ? tree.documents.filter((doc) => doc.filename.toLowerCase().includes(normalizedSearch))
    : tree.documents;

  const isEmpty = tree.folders.length === 0 && visibleDocuments.length === 0;

  return (
    <div>
      {/* Breadcrumb */}
      {currentFolder && (
        <FolderBreadcrumb path={currentFolder} onNavigate={setCurrentFolder} />
      )}

      {/* Empty state */}
      {isEmpty && (
        <Card className="p-8 text-center">
          <Icon name="folder-open" size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {currentFolder
              ? t('admin.knowledge.documents.emptyFolder')
              : t('admin.knowledge.documents.noDocuments')}
          </p>
          {currentFolder && (
            <Button variant="link" className="mt-2" onClick={() => setCurrentFolder('')}>
              {t('admin.knowledge.documents.backToRoot')}
            </Button>
          )}
        </Card>
      )}

      {/* Folders first, then documents */}
      <div className="space-y-2">
        {tree.folders.map((f) => (
          <FolderRow key={f.path} folder={f} onOpen={setCurrentFolder} />
        ))}
        {visibleDocuments.map((doc) => (
          <div key={doc.id}>{renderDocumentRow(doc)}</div>
        ))}
      </div>
    </div>
  );
}
